import React, { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom';
import { Button, MenuItem, Select } from '@mui/material';

import { IReportRegistration } from '../../types/report';

import { UtilityService } from '../../services/utilityService';

import { Icons } from '../icons';
import { AppConfig } from '../../config/appConfig';

export interface IReportRegistrationTableProps {
	data?: IReportRegistration[];	
	displayName?: string;
	name?: string;
	powerBiId?: string;
	active?: number;
	listed?: number;
	onUnregisterRequest?: (id: number) => void;
	onOrdinalChangeRequest?: (id: number, ordinal: number) => void;
}

const ReportRegistrationTable: React.FunctionComponent<IReportRegistrationTableProps> = ({ data, displayName, name, powerBiId, active, listed, onUnregisterRequest, onOrdinalChangeRequest }) => {
	const [dataView, setDataView] = useState<IReportRegistration[]>([]);
	const [unfilterdData, setUnfilteredData] = useState<IReportRegistration[]>([]);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(AppConfig.reportRegistrationListDefaultPageSize);
	const [sortField, setSortField] = useState(AppConfig.reportRegistrationListDefaultSortField);
	const [sortAscending, setSortAscending] = useState(true);

	const handleHeaderClick = (field: string) => {
		if (sortField == field) {
			setSortAscending(!sortAscending);
		}
		else {
			setSortAscending(true);
			setSortField(field);
		}
	}

	useEffect(() => {
		if (data) {
			let newData = data.filter((item) => {
				if (displayName?.trim() && !item.displayName?.toLowerCase()?.trim()?.includes(displayName.toLowerCase()?.trim())) {
					return false;
				}
				if (name?.trim() && !item.name?.toLowerCase()?.trim()?.includes(name.toLowerCase()?.trim())) {
					return false;
				}
				if (powerBiId?.trim() && ((!item.reportId?.toLowerCase()?.trim()?.includes(powerBiId?.toLowerCase()?.trim())) && (!item.workspaceId?.toLowerCase()?.trim()?.includes(powerBiId?.toLowerCase()?.trim())))) {
					return false;
				}
				if (active != -1 && !!active != item.active) {
					return false;
				}
				if (listed != -1 && ((!!listed && item.ordinal <= 0) || (!listed && item.ordinal > 0))) {
					return false;
				}

				return true;
			}).slice();

			// Start sort as descending if the field name starts with "-"
			let _sortField = sortField;
			if (sortField.startsWith("-")) {
				setSortAscending(false);
				_sortField = sortField.substring(1);
				setSortField(_sortField);
			}

			if (sortField) {
				switch (sortField) {
					case "ordinal": {
						newData.sort((a, b) => UtilityService.compareNumbers(a.ordinal, b.ordinal));
						break;
					}
					case "displayName": {
						newData.sort((a, b) => UtilityService.compareStrings(a.displayName, b.displayName));
						break;
					}
					case "permission": {
						newData.sort((a, b) => UtilityService.compareStrings(a.permission?.toLowerCase(), b.permission?.toLowerCase()));
						break;
					}
					default: {
						newData.sort((a, b) => UtilityService.compareNumbers(a.ordinal, b.ordinal));
						break;
					}
				}

				if (!sortAscending) newData.reverse();
				setDataView(newData);
				setUnfilteredData(structuredClone(data).sort((a, b) => UtilityService.compareNumbers(a.ordinal, b.ordinal)));
			}
		}
	}, [data, sortField, sortAscending, displayName, name, powerBiId, active, listed]);

	const handlePageBack = () => {
		setPage(page - 1);
	}

	const handlePageForward = () => {
		setPage(page + 1);
	}

	return (
		<div className="hwi-entity-search-results-table">
			<div className="headers">
				<div className="ordinal-col">
					<p onClick={() => handleHeaderClick("ordinal")}>
						Ordinal
						{" "}
						{(sortField == "ordinal" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "ordinal" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="display-name-col">
					<p onClick={() => handleHeaderClick("displayName")}>
						Name
						{" "}
						{(sortField == "displayName" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "displayName" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="power-bi-ids-col no-sort">
					<p>
						Power BI IDs
					</p>
				</div>
				<div className="permission-col">
					<p onClick={() => handleHeaderClick("permission")}>
						Permission
						{" "}
						{(sortField == "permission" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
						{(sortField == "permission" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
					</p>
				</div>
				<div className="active-col no-sort">
					<p>
						Active
					</p>
				</div>
				<div className="controls-col">{""}</div>
			</div>
			<div className="data">
				{dataView?.length > 0 ?
					(
						<>
							{dataView?.slice(page * pageSize, (page + 1) * pageSize).map((item) => (
								<div className='row' key={item.id}>
									<div className="ordinal-col">
										{item.active ? (
										<Select
											value={item.ordinal}
											onChange={(event) => onOrdinalChangeRequest?.(item.id, +event.target.value)}
										>
											<MenuItem value={0}>Unlisted</MenuItem>
											{unfilterdData.filter(rr => rr.active && rr.ordinal > 0).map((rr) => (
												<MenuItem key={rr.id} value={rr.ordinal}>{rr.ordinal}</MenuItem>
											))}
										</Select>
										) : (
											<p>{item.ordinal}</p>
										)}
									</div>
									<div className="display-name-col">
										<p>
											<div title="Display Name">&quot;{item.displayName}&quot;</div>
											<div title="Power BI Report Name" className="monospace">{item.name}</div>
										</p>
									</div>
									<div className="power-bi-ids-col monospace">
										<p>
											<div title='Power BI Report ID'>R: {item.reportId}</div>
											<div title='Power BI Workspace ID'>W: {item.workspaceId ?? "-"}</div>
										</p>
									</div>
									<div className="permission-col"><p>{item.permission ?? "-"}</p></div>
									<div className="active-col"><p>{item.active ? "Active" : "-"}</p></div>
									<div className="controls-col"><p>
										<NavLink to={"/report/registration/" + item.id}>
											<Icons.EditItem fill="#272f50" height="14px"></Icons.EditItem>
											{" "}
											Edit
										</NavLink>
										<div className='delete' onClick={() => onUnregisterRequest?.(item.id)}>
											<Icons.RemoveItem height="14px"></Icons.RemoveItem>
											{" "}
											Remove
										</div>
									</p></div>
								</div>
							))}
						</>
					) : (<div className="no-results">0 results</div>)}
			</div>
			{dataView?.length > 0 &&
				<div className="pager">
					<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={page < 1} onClick={handlePageBack}>Previous</Button></div>
					<div className="pager-text">{`${(page * pageSize) + 1} to ${((page + 1) * pageSize) <= dataView.length ? ((page + 1) * pageSize) : dataView.length} of ${dataView.length}`}</div>
					<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={((page + 1) * pageSize) >= dataView.length} onClick={handlePageForward}>Next</Button></div>
				</div>
			}
		</div>
	)
}

export default ReportRegistrationTable;